import * as React from "react";
import { Panel, PanelType, Stack, DatePicker, Dropdown, IDropdownOption, Spinner, MessageBar, DetailsList, TooltipHost, Icon, MessageBarType, SelectionMode, IColumn, ActionButton } from "office-ui-fabric-react";
import { loadLocations, getSites, getSitesByRoomType } from "../../../api/siteslocations";
import { ILocation, IRoom, ISite, RoomType } from "../../../types/SiteLocation.types";
import { loadRoomsBySite, loadRoomsByLocation } from "../../../api/rooms";
import { IRoomAvailability } from "../../../types/Booking.types";
import CalendarView from "./CalendarView";
import { DefaultButton } from "@fluentui/react";
import { SiteDayCalendar } from "./SiteDayCalendar";

export interface ISiteLocRoomListProps {
    roomType: RoomType;
}

export const SiteLocRoomList = (props:ISiteLocRoomListProps) => {
    const [siteOptions, setSiteOptions] = React.useState<IDropdownOption[]>([]);
    const [locationOptions, setLocationOptions] = React.useState<IDropdownOption[]>([]);
    const [sites, setSites] = React.useState<ISite[]>([]);
    const [selectedSite, setSelectedSite] = React.useState<number>();
    const [selectedLocation, setSelectedLocation] = React.useState<number>();
    const [rooms, setRooms] = React.useState<IRoom[]>();
    const [selectedRoom, setSelectedRoom] = React.useState<IRoom>();
    const [resultsLoading, setResultsLoading] = React.useState(false);
    const [roomCalendar, setRoomCalendar] = React.useState<IRoom>();
    const [siteDayPanelOpen, setSiteDayPanelOpen] = React.useState(false);

    const _load = async (abortController: AbortController) => {
        const siteResponse = await getSitesByRoomType(props.roomType, abortController);
        if (siteResponse.ok) {
            const siteList = await siteResponse.json() as ISite[];
            setSites(siteList);
            const siteDropdownOptions:IDropdownOption[] = siteList.filter((s) => s.RoomBookingEnabled).map((s) => {
                return {
                    key: s.Id,
                    text: s.Title
                }
            });
            setSiteOptions(siteDropdownOptions);
        }
    }

    const _getLocations = React.useCallback(async (abortController: AbortController) => {
        if (selectedSite === undefined) return;
        setSelectedLocation(undefined);
        const locationResponse = await loadLocations(selectedSite);
        if (locationResponse.ok) {
            const locations = await locationResponse.json() as ILocation[];
            const locationDropdownOptions:IDropdownOption[] = locations.map((s) => {
                return {
                    key: s.Id,
                    text: s.Title
                }
            });
            setLocationOptions(locationDropdownOptions);
        }
    }, [selectedSite]);

    const _getRooms = React.useCallback(async (abortController: AbortController) => {
        if (selectedSite) {
            setResultsLoading(true);
            const response = await (selectedLocation ? loadRoomsByLocation(props.roomType, selectedLocation) : loadRoomsBySite(props.roomType, selectedSite));
            if (response.ok) {
                setRooms(await response.json() as IRoom[]);
            }
            setResultsLoading(false);
        }
    }, [props.roomType, selectedSite, selectedLocation]);

    React.useEffect(() => {
        const abortController = new AbortController();
        _getRooms(abortController);

        return () => {
            abortController.abort();
        };
    }, [_getRooms]);

    React.useEffect(() => {
        const abortController = new AbortController();
        _getLocations(abortController);

        return () => {
            abortController.abort();
        };
    }, [_getLocations]);

    // On Mount
    React.useEffect(() => {
        const abortController = new AbortController();
        _load(abortController);

        // If unmounting, cancel any async queries
        return () => {
            abortController.abort();
        };
    }, []);

    const columns = React.useMemo(() => ([
        {
            key: "name",
            fieldName: "Name",
            name: "Room",
            minWidth: 180
        },
        {
            key: "capacity",
            fieldName: "Capacity",
            name: "Capacity",
            minWidth: 80
        },
        {
            key: "equipment",
            fieldName: "Equipment",
            name: "Equipment",
            isIconOnly: true,
            iconName: "Info",
            minWidth: 50,
            onRender: (item: IRoom) => {
                if (item.Equipment) {
                    return (
                        <TooltipHost content={item.Equipment}>
                            <Icon iconName="Info" />
                        </TooltipHost>
                    )
                }
            }
        },
        {
            key: "calendarBtn",
            fieldName: "Id",
            name: "View Calendar",
            isIconOnly: true,
            iconName: "Calendar",
            minWidth: 150,
            onRender: (item: IRoom) => {
                return (
                    <ActionButton 
                        styles={{flexContainer: { alignItems: "flex-start" }}} 
                        iconProps={{ iconName: "Calendar" }} 
                        text="View Calendar" 
                        onClick={() => {
                            setRoomCalendar(item);
                        }}
                    />
                );
            }
        }
    ] as IColumn[]), []);

    let selectedSiteInfo: JSX.Element | null = null;
    if (selectedSite) {
        const site = sites?.find((s) => s.Id === selectedSite);
        if (site && site.Notes) {
            selectedSiteInfo = <MessageBar styles={{root: { marginTop: 20}}} messageBarType={MessageBarType.info}>{site.Notes}</MessageBar>;
        }
    }

    return (
        <>
            <Stack tokens={{ childrenGap: 10 }}>
                <Stack horizontal horizontalAlign="stretch" tokens={{childrenGap: 10}}> 
                    <Dropdown styles={{root: { flexGrow: 1}}} label="Site" placeholder="Select a site" options={siteOptions} onChange={(event, option, index) => { if (option) setSelectedSite(option.key as number)}} />
                    <Dropdown styles={{root: { flexGrow: 1}}} label="Location" placeholder="Select a location" disabled={!selectedSite} options={locationOptions} onChange={(event, option, index) => { if (option) setSelectedLocation(option.key as number)}} />
                    {/* <Dropdown label="Desk" placeholder="Select a desk" disabled={!chosenSite} options={deskOptions} onChange={(event, option, index) => { if (option) setChosenDesk(option.key as number)}} /> */}
                </Stack>
            </Stack>
            { selectedSiteInfo }
            { selectedSite && (
                <DefaultButton styles={{root: { marginTop: 20}}} text="Site Day View" onClick={() => { setSiteDayPanelOpen(true); }} />
            )}
            { resultsLoading === true && (
                <Spinner label="Loading results" />
            )}
            {rooms !== undefined && <>
                {rooms.length === 0 && (
                    <MessageBar messageBarType={MessageBarType.info}>No rooms were found, but this may be due to booking restrictions in effect at this location.</MessageBar>
                )}
                <DetailsList
                    items={rooms}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                />
            </>}
            { roomCalendar !== undefined && (
                <CalendarView
                    onDismiss={() => setRoomCalendar(undefined)}
                    roomId={roomCalendar.Id}
                    roomName={roomCalendar.Name}
                    workWeekOnly={selectedSite ? sites?.find((s) => s.Id === selectedSite)?.WorkWeekOnly : false}
                />
            )}
            { siteDayPanelOpen && (
                <Panel type={PanelType.large} customWidth="900px"
                    isOpen={siteDayPanelOpen} headerText="Site Day View"
                    onDismiss={() => setSiteDayPanelOpen(false)}
                >
                    <SiteDayCalendar siteId={selectedSite || -1} roomType={props.roomType} />
                </Panel>
            )}
        </>
    )
};

export default SiteLocRoomList;